import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ProgressBar from '../components/ProgressBar';
import ColdImage from '../images/Cold.png';
import NeutralImage from '../images/Neutral.png';
import NotSureImage from '../images/Not sure.png';
import popupImage from '../images/popup.png';
import WarmImage from '../images/Warm.png';
import Popup from './Popup';
import './SelectUnderTone.css';

const SelectUnderTone = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedUnderTone, setSelectedUnderTone] = useState({});
    const [currentStep] = useState(5);
    const [skinUnderToneList, setSkinUnderToneList] = useState([]);
    const [showSkipPopup, setShowSkipPopup] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const isFromProfile = location.state?.fromProfile; // Check if redirected from profile

    useEffect(() => {
        getSkinUnderToneOptions();

        // Retrieve pre-selected values from sessionStorage
        const storedSkinUnderTone = sessionStorage.getItem('skin-underTone') || {};
        let parsedUnderTone;
        try {
            parsedUnderTone = JSON.parse(storedSkinUnderTone);
        } catch (error) {
            parsedUnderTone = {};
        }
        setSelectedUnderTone(parsedUnderTone);
    }, []);

    // Handle the selection of undertone
    const handleSelectUnderTone = (uId, uName) => {
        const undertone = { id: uId, name: uName };
        setSelectedUnderTone(undertone);

        if (uName === "Not sure") {
            getQAFormQuestions();  // Call the function to fetch questions
        } else {
            sessionStorage.setItem('skin-underTone', JSON.stringify(undertone)); // Store selected undertone in sessionStorage
            console.log('Stored undertone in sessionStorage:', undertone);
        }
    };

    // Fetch skin undertone options from API
    const getSkinUnderToneOptions = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                "https://dev-api.curu.com.au/api/skin-undertone",
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                const errorResponse = await response.json();
                throw new Error(`Failed to get skin undertone options: ${errorResponse.message || response.statusText}`);
            }

            const skinUnderTones = await response.json();

            setSkinUnderToneList(skinUnderTones.map(item => ({
                id: item.id,
                name: item.name,
                description: item.description
            })));
        } catch (error) {
            console.error(error.message);
            throw error;
        }
    };

    // Show confirmation popup when skipping undertone selection
    const handleSkipClick = () => {
        setShowSkipPopup(true);
    };

    // Confirm skip and navigate to home page
    const handleSkipConfirm = () => {
        setShowSkipPopup(false);
        navigate('/home-page');
    };

    // Update selected undertone in profile database and show success popup
    const handleUpdateUnderToneClick = async () => {
        const storedSkinUnderTone = sessionStorage.getItem('skin-underTone');

        // Helper function to parse stored value and retrieve ID
        const parseAndExtractId = (storedValue) => {
            try {
                const parsedValue = JSON.parse(storedValue);
                return parsedValue?.id || null;
            } catch {
                return null;
            }
        };

        const storedSkinUnderToneId = parseAndExtractId(storedSkinUnderTone)

        const quizData = {
            skin_undertone: storedSkinUnderToneId
        };

        console.log('Skin quiz data to store in database:', quizData);

        try {
            const response = await fetch('https://dev-api.curu.com.au/api/profile', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(quizData)
            });

            if (!response.ok) {
                throw new Error('Failed to save the skin quiz data.');
            }

            setShowPopup(true); // Show success popup
            // Optionally navigate after successful update
            navigate('/user-profile'); // Change this to your desired route after update
        } catch (error) {
            console.error('Error saving quiz data:', error.message);
            alert('There was a problem saving your quiz data. Please try again.');
        }
    };

    // Helper function to check if quiz is complete
    const checkIfQuizComplete = (quizData) => {
        return quizData.skin_type && 
               quizData.skin_concerns && 
               quizData.skin_concerns.length > 0 &&
               quizData.skin_sensitive_type && 
               quizData.skin_undertone && 
               quizData.skin_tone;
    };

    // Handle 'Next' button click to save quiz data and proceed
    const handleNextClick = async () => {
        // Retrieve data from session storage
        const storedSkinType = sessionStorage.getItem('skin-type');
        const selectedSkinConcerns = JSON.parse(sessionStorage.getItem('skin-concerns')) || [];
        const storedSkinSensitivity = sessionStorage.getItem('skin-sensitivity');
        const storedSkinTone = sessionStorage.getItem('skin-tone');

        // Helper function to parse and extract IDs from stored values
        const parseAndExtractId = (storedValue) => {
            try {
                const parsedValue = JSON.parse(storedValue);
                return parsedValue?.id || null;
            } catch {
                return null;
            }
        };

        const skinTypeId = parseAndExtractId(storedSkinType);
        const skinSensitivityId = parseAndExtractId(storedSkinSensitivity);
        const skinToneId = parseAndExtractId(storedSkinTone);
        const skinConcernIds = selectedSkinConcerns.length > 0
            ? selectedSkinConcerns.map(concern => concern.id)
            : null;

        // Map retrieved data to quiz data object
        const quizData = {
            skin_type: skinTypeId,
            skin_sensitive_type: skinSensitivityId,
            skin_concerns: skinConcernIds,
            skin_tone: skinToneId,
            skin_undertone: selectedUnderTone?.id || null
        };

        console.log('Skin quiz data to store in database:', quizData);

        try {
            const response = await fetch('https://dev-api.curu.com.au/api/profile', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(quizData)
            });

            if (!response.ok) {
                throw new Error('Failed to save the skin quiz data.');
            }

            // Check if this completion makes the quiz fully complete
            const isQuizComplete = checkIfQuizComplete(quizData);
            
            if (isQuizComplete && !isFromProfile) {
                console.log("🎉 Full skin quiz completed for the first time! Setting completion flag.");
                sessionStorage.setItem('justCompletedSkinQuiz', 'true');
            }

            setShowPopup(true);
        } catch (error) {
            console.error('Error saving quiz data:', error.message);
            alert('There was a problem saving your quiz data. Please try again.');
        }
    };

    // Navigate back to previous page
    const handleBackClick = () => {
        navigate("/select-skintone");
    };

    // Close popup and navigate to home page
    const handleClosePopup = () => {
        setShowPopup(false);
        navigate('/home-page');
    };

    // Map undertone names to respective images
    const mapSkinUnderToneImages = {
        "Cool": ColdImage,
        "Warm": WarmImage,
        "Neutral": NeutralImage,
        "Not sure": NotSureImage
    };

    // Fetch questions for "Not sure" selection
    const getQAFormQuestions = async () => {
        try {
            let token = localStorage.getItem('token');

            const response = await fetch('https://dev-api.curu.com.au/api/questions?type=1', {
                method: 'GET',
                headers: {
                    'accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (response.ok) {
                const data = await response.json();
                navigate('/find-undertone', { state: { questions: data, fromProfile: isFromProfile } });
            } else {
                console.error('Failed to fetch questions');
            }
        } catch (error) {
            console.error(error.message);
        }
    };

    return (
        <div className="skin-undertone-container">
            <div className="skin-undertone-box">
                {/* Show skip button only if not redirected from Profile */}
                {!isFromProfile && ( // Only show skip button if not from Profile page
                    <button className="skip-link-skin-undertone" onClick={handleSkipClick}>Skip</button>
                )}
                <ProgressBar currentStep={currentStep} comboType="combo2" />
                <div className="title-container-skin-undertone">
    <h1 className="title-skin-undertone">Select Your Undertone</h1>
</div>
                <div className="underTone-options">
                    {/* Map undertone options */}
                    {skinUnderToneList.map((underTone) => (
                        <div
                            key={underTone.id}
                            className={`option ${selectedUnderTone.id === underTone.id ? 'selected' : ''}`}
                            onClick={() => handleSelectUnderTone(underTone.id, underTone.name)}
                        >
                            <img src={mapSkinUnderToneImages[underTone.name]} alt={underTone.name} className="icon" />
                            <div className="undertone-content">
                                <label htmlFor={`underTone-option-${underTone.id}`} className="radio-label">
                                    <input
                                        type="radio"
                                        name="skinUnderTone"
                                        checked={selectedUnderTone.name === underTone.name}
                                        id={`underTone-option-${underTone.id}`}
                                        className="radio-input"
                                    />
                                    <span className="underTone-name">{underTone.name}</span>
                                </label>
                                <div className="underTone-description">{underTone.description}</div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="buttons-container-skin-undertone">
                    {/* Display buttons based on navigation source */}
                    {isFromProfile ? (
                        <>
                            <button
  className="close-button-skin-sensitivity"
  onClick={() => navigate('/user-profile')}
>
                         Close
                       </button>
                        <button className="update-button-skin-undertone" onClick={handleUpdateUnderToneClick} disabled={!selectedUnderTone.name}>
                            Update
                        </button>
                        </>
                    ) : (
                        <>
                            <button className="back-button-skin-undertone" onClick={handleBackClick}>Back</button>
                            <button
                                className={`next-button-skin-undertone ${!selectedUnderTone.id ? 'disabled' : ''}`}
                                onClick={handleNextClick}
                                disabled={!selectedUnderTone.id}
                            >
                                Next
                            </button>
                        </>
                    )}
                </div>
            </div>
            {/* Popup Components */}
            {showPopup && (
                <div className="popup">
                    <div className="popup-content">
                        <img src={popupImage} alt="User" className="popup-image-skin-under-tone-QA" />
                        <p className='popup-message-skin-undertone'>You've completed the skin quiz, enjoy browsing products curated for you:</p>
                        <button onClick={handleClosePopup}>Close</button>
                    </div>
                </div>
            )}
            <Popup
                isVisible={showSkipPopup}
                title="Skip Skin Under Tone"
                onClose={() => setShowSkipPopup(false)}
                onConfirm={handleSkipConfirm}
            />
        </div>
    );
};

export default SelectUnderTone;